// @flow
/* eslint-disable no-plusplus */
import {
  nAry,
  compose,
  keys,
  length,
  prepend,
  take,
  drop,
  curry,
  isEmpty,
  any,
  memoize,
  pipe,
  of,
  ap,
  apply
} from 'ramda';
import type { Reducer } from '../types';
import { isJp } from '../components/Search/helpers';

export const groupsOf = curry(function group(n, list: any) {
  return isEmpty(list) ? [] : prepend(take(n, list), group(n, drop(n, list)));
});

export const objSize = nAry(
  1,
  compose(
    length,
    keys
  )
);

/**
 * makeActionCreator is a boilerplate helper for redux,
 * which should be used when creating non-async actions.
 * usage: const actionCreator = makeActionCreator(CONSTANT_NAME, 'paramName1', 'paramName2');
 * @param type
 * @param argNames
 */
export const makeActionCreator = (type: string, ...argNames: Array<string>) => (
  ...args: Array<mixed>
): Object => {
  const action = { type };
  argNames.forEach((arg, index) => {
    action[argNames[index]] = args[index];
  });
  return action;
};

/**
 * Reduce is a switch replacement/boilerplate helper for redux.
 * It helps with flow type consistency.
 * @param initialState
 * @param handlers
 * @returns {reducer}
 */
export function reduce<S, A: *>(
  initialState: S,
  handlers: { [key: string]: Reducer<S, A> }
): Reducer<S, A> {
  return function reducer(state: S = initialState, action: A): S {
    return Object.prototype.hasOwnProperty.call(handlers, action.type)
      ? handlers[action.type](state, action)
      : state;
  };
}

const bad = fns => any(fn => typeof fn !== 'function')(fns);
const fnsOrArray = fns => (Array.isArray(fns[0]) ? fns[0] : fns.slice(0, -1));

/**
 * createSelector is a selector helper like reselect.js - useful for computed properties from state
 * - it memoizes its args and their return so computation will only ever be performed once for
 * the same args and state
 * @param fns
 */
export const createSelector = (...fns: Array<Function>) => (
  state: any,
  props: any
) => {
  const preFns = fnsOrArray(fns).map(fn => stateAndProps =>
    fn(...stateAndProps)
  );

  if (bad(preFns)) {
    return () => {
      throw new Error('reselect: input-selectors must be a function');
    };
  }

  const lastFn = memoize(fns[fns.length - 1]);

  return pipe(
    of,
    ap(preFns),
    apply(lastFn)
  )([state, props]);
};

export const getQueryParam = (search: string, name: string) => {
  const match = RegExp(`[?&]${name}=([^&]*)`).exec(search);
  return match && decodeURIComponent(match[1].replace(/\+/g, ' '));
};

export const getCookieValue = (name: string) => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
};

export const dateFromPair = (day: Date, time: string) => {
  const date = new Date(day);
  const [hours, minutes, seconds] = time
    .split(':')
    .map(str => parseInt(str, 10));
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(seconds);
  return date;
};

export const availabilityString = (availability: number) => {
  if (availability < 33) {
    return 'Unlikely';
  }
  if (availability < 66) {
    return 'Moderate';
  }
  if (availability < 95) {
    return 'Likely';
  }
  return "We're certain";
};

export const availabilityColor = (availability: number) => {
  if (availability >= 0.8) {
    return '#0a0';
  }
  if (availability >= 0.5) {
    return '#fab03a';
  }
  return '#fa4949';
};

export const milesToMeters = (miles: number) => Math.round(miles * 1609);

const spreadKey = (category: string, radius: number) =>
  `${category}_${radius}m`;

export const resultSpread = (results: array) => {
  const spread = {};
  const categories = ['jp', 'aux', 'all'];
  const radii = [100, 250, 400, 550, 700];

  categories.forEach(cat => {
    radii.forEach(rad => {
      spread[spreadKey(cat, rad)] = 0;
    });

    spread[`${cat}_total`] = 0;
  });

  results.forEach(result => {
    const cat = isJp(result) ? 'jp' : 'aux';

    radii.forEach(radius => {
      if (result.distanceInMeters <= radius) {
        spread[spreadKey(cat, radius)]++;
        spread[spreadKey('all', radius)]++;
      }
    });

    spread[`${cat}_total`]++;
    spread.all_total++;
  });

  return spread;
};

export default {
  groupsOf,
  objSize,
  makeActionCreator,
  reduce,
  createSelector,
  getQueryParam,
  dateFromPair,
  availabilityString
};
